import * as React from 'react';
import type { CampaignContextData } from 'shared/src/api/ai-line-item-chat-types';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import { InfoPanelFrame } from '@/features/ai/shared/info-panel-frame';

type CampaignContextCardProps = {
  data: CampaignContextData;
};

// Collapsed-row facts: the numbers users glance at, so the panel rarely needs opening.
function summaryDetail(data: CampaignContextData): string {
  const items = data.existing_line_items.length;
  const itemsPart = `${items} line item${items === 1 ? '' : 's'}`;
  if (data.media_budget !== undefined) {
    const media = data.media_budget != null ? formatMoney(data.media_budget) : '—';
    return `$${data.proposal_budget.toLocaleString()} price · ${media} media · ${itemsPart}`;
  }
  return `$${data.proposal_budget.toLocaleString()} · $${data.remaining_budget.toLocaleString()} remaining · ${itemsPart}`;
}

export function CampaignContextCard({ data }: CampaignContextCardProps) {
  return (
    <InfoPanelFrame title={data.campaign_name} detail={summaryDetail(data)}>
      <div className="mb-3 text-xs text-gray-500">{data.account_name}</div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <ContextField label="Flight Dates" value={data.flight_dates} />
        <ContextField label="Goals / KPIs" value={data.goals_kpis} />
        <ContextField
          label={data.media_budget !== undefined ? 'Price' : 'Proposal Budget'}
          value={`$${data.proposal_budget.toLocaleString()}`}
        />
        <ContextField label="Proposal Dates" value={data.proposal_dates} />
        <ContextField
          label="Existing Items"
          value={
            data.existing_line_items.length > 0
              ? `${data.existing_line_items.length} ($${data.existing_line_items_total.toLocaleString()})`
              : 'None'
          }
        />
        {data.media_budget !== undefined ? (
          <ContextField
            label="Media Budget"
            value={data.media_budget != null ? formatMoney(data.media_budget) : 'Not derivable'}
            highlight
          />
        ) : (
          <ContextField
            label="Remaining Budget"
            value={`$${data.remaining_budget.toLocaleString()}`}
            highlight
          />
        )}
      </dl>

      {data.zoho_fields.length > 0 ? (
        <div className="mt-3 border-t pt-3">
          <h4 className="mb-2 text-xs font-semibold text-gray-500">CRM DATA</h4>
          <dl className="space-y-1 text-xs">
            {data.zoho_fields.map((field, i) => (
              <div key={i}>
                <dt className="text-gray-500">{field.label}</dt>
                <dd className="font-medium text-gray-900">{field.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}

      {data.existing_line_items.length > 0 ? (
        <div className="mt-3 border-t pt-3">
          <h4 className="mb-2 text-xs font-semibold text-gray-500">EXISTING LINE ITEMS</h4>
          <div className="space-y-1 text-xs">
            {data.existing_line_items.map((li, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-gray-900">{li.name}</span>
                <span className="text-gray-500">
                  ${li.price.toLocaleString()} — {li.detail}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </InfoPanelFrame>
  );
}

// --- Private helpers ---

function ContextField({
  label,
  value,
  highlight
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <>
      <dt className="text-gray-500">{label}</dt>
      <dd className={highlight ? 'font-semibold text-green-700' : 'font-medium text-gray-900'}>
        {value}
      </dd>
    </>
  );
}
